// screens/AdminToolsScreen.js
import React, { useEffect, useState } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity,
  ActivityIndicator, Alert, ScrollView
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../../firebaseConfig';
import {
  setDoc, collection, doc, getDoc, getDocs,
  query, serverTimestamp, where, limit, writeBatch
} from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';

import Footer from '../components/Footer';
import NavBar from '../components/NavBar';

import InviteCodeAdminPanel from '../components/InviteCodeAdminPanel';

// Admin tools
import TeamTournamentTester from './TeamTournamentTester';

/* ------ helpers: custom IDs w/ suffix so they're easy to spot ------ */
const newTestMatchId = (dbRef) => `${doc(collection(dbRef, 'matches')).id}-testMatch`;
const newTestTourneyId = (dbRef) => `${doc(collection(dbRef, 'tournaments')).id}-testTourney`;

const COLORS = {
  bg: '#000000',
  card: '#111111',
  cardAlt: '#0a0a0a',
  border: '#1f1f1f',
  borderLight: '#2a2a2a',
  primary: '#FCD000',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  text: '#ffffff',
  textMuted: '#888888',
  textDim: '#555555',
};

export default function AdminToolsScreen({ navigation }) {
  const auth = getAuth();
  const functions = getFunctions();

  // --- Admin guard ---
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [guardLoading, setGuardLoading] = useState(true);
  
  // Section expansion state
  const [expandedSections, setExpandedSections] = useState({
    inviteCodes: true,
    teamTournament: true,
    platformDisputes: false,
    testData: false,
    maintenance: false,
  });

  // Platform account disputes state
  const [platformDisputes, setPlatformDisputes] = useState([]);
  const [disputesLoading, setDisputesLoading] = useState(false);
  const [resolvingDispute, setResolvingDispute] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUser(null);
        setIsAdmin(false);
        setGuardLoading(false);
        navigation.replace('Landing');
        return;
      }
      setUser(u);
      try {
        const token = await u.getIdTokenResult(true);
        const claimAdmin = token?.claims?.role === 'admin';
        const snap = await getDoc(doc(db, 'users', u.uid));
        const docAdmin = snap.exists() && snap.data()?.role === 'admin';
        setIsAdmin(claimAdmin || docAdmin);
      } catch {
        setIsAdmin(false);
      } finally {
        setGuardLoading(false);
      }
    });
    return unsub;
  }, [auth, navigation]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Platform disputes handlers
  const fetchPlatformDisputes = async () => {
    setDisputesLoading(true);
    try {
      const fn = httpsCallable(functions, 'adminGetPlatformAccountDisputes');
      const result = await fn();
      setPlatformDisputes(result.data.disputes || []);
    } catch (e) {
      Alert.alert('Error', e.message);
    } finally {
      setDisputesLoading(false);
    }
  };

  const handleResolveDispute = async (disputeId, resolution) => {
    const labels = {
      transfer: 'Transfer the account to the person who filed the dispute',
      keep_current: 'Keep the current holder (dismiss dispute)',
      remove_both: 'Remove the claim from both users',
    };

    Alert.alert(
      'Resolve Dispute',
      labels[resolution],
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Confirm',
          onPress: async () => {
            setResolvingDispute(disputeId);
            try {
              const fn = httpsCallable(functions, 'adminResolvePlatformAccountDispute');
              await fn({ disputeId, resolution, adminNotes: '' });
              setPlatformDisputes(prev => prev.filter(d => d.id !== disputeId));
              Alert.alert('Resolved', `Dispute resolved: ${resolution}`);
            } catch (e) {
              Alert.alert('Error', e.message);
            } finally {
              setResolvingDispute(null);
            }
          }
        }
      ]
    );
  };

  if (guardLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.center}>
          <ActivityIndicator color={COLORS.primary} />
          <Text style={styles.dim}>Checking admin permissions...</Text>
        </View>
      </SafeAreaView>
    );
  }
  
  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.center}>
          <Ionicons name="lock-closed" size={48} color={COLORS.danger} />
          <Text style={styles.warn}>Admin access required.</Text>
          <TouchableOpacity onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))} style={styles.backBtn}>
            <Ionicons name="arrow-back" size={16} color={COLORS.textMuted} />
            <Text style={styles.backText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  /* ---------------- actions ---------------- */

  const seedTestData = async () => {
    try {
      const uid = user.uid;
      const now = Date.now();

      const baseMatch = {
        isTest: true,
        createdBy: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        game: 'Marvel Rivals',
        platform: 'PC',
        region: 'NA',
        teamSize: 1,
        maxEntries: 2,
        participants: [{ username: 'alpha' }, { username: 'bravo' }],
      };

      // Active x3
      for (let i = 1; i <= 3; i++) {
        await setDoc(doc(db, 'matches', newTestMatchId(db)), {
          ...baseMatch, status: 'active', name: `Active Test ${i}`,
        });
      }

      // Awaiting verification x2
      for (let i = 1; i <= 2; i++) {
        await setDoc(doc(db, 'matches', newTestMatchId(db)), {
          ...baseMatch, status: 'awaiting_verification', name: `Review Test ${i}`,
        });
      }

      // Ready for payout x1
      await setDoc(doc(db, 'matches', newTestMatchId(db)), {
        ...baseMatch, status: 'ready_for_payout', name: 'Payout Test 1',
      });

      // Tournaments
      await setDoc(doc(db, 'tournaments', newTestTourneyId(db)), {
        isTest: true,
        name: 'Index Test Tourney (Today)',
        status: 'scheduled',
        startTime: new Date(now + 60 * 60 * 1000),
        registrationCloseTime: new Date(now + 30 * 60 * 1000),
        minEntries: 8,
        participantsCount: 0,
        createdBy: uid,
        createdByAdmin: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await setDoc(doc(db, 'tournaments', newTestTourneyId(db)), {
        isTest: true,
        name: 'Sweep Cancel Sample',
        status: 'scheduled',
        startTime: new Date(now + 10 * 60 * 1000),
        registrationCloseTime: new Date(now - 5 * 60 * 1000),
        minEntries: 64,
        participantsCount: 8,
        createdBy: uid,
        createdByAdmin: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await setDoc(doc(db, 'tournaments', newTestTourneyId(db)), {
        isTest: true,
        name: 'Sweep Go Live Sample',
        status: 'scheduled',
        startTime: new Date(now - 1 * 60 * 1000),
        registrationCloseTime: new Date(now - 10 * 60 * 1000),
        minEntries: 2,
        participantsCount: 2,
        createdBy: uid,
        createdByAdmin: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      Alert.alert('Seeded', 'Test matches and tournaments were created.');
    } catch (e) {
      console.error(e);
      Alert.alert('Error', `Failed to seed test data.\n${e?.code || ''} ${e?.message || ''}`);
    }
  };

  const clearSeedData = async () => {
    try {
      const uid = user.uid;
      const batch = writeBatch(db);

      const qs1 = await getDocs(query(
        collection(db, 'matches'),
        where('isTest', '==', true),
        where('createdBy', '==', uid),
        limit(200)
      ));
      qs1.forEach(d => batch.delete(doc(db, 'matches', d.id)));

      const qs2 = await getDocs(query(
        collection(db, 'tournaments'),
        where('isTest', '==', true),
        where('createdBy', '==', uid),
        limit(200)
      ));
      qs2.forEach(d => batch.delete(doc(db, 'tournaments', d.id)));

      await batch.commit();
      Alert.alert('Cleared', 'Deleted seeded test items.');
    } catch (e) {
      console.error(e);
      Alert.alert('Error', 'Failed to clear test data.');
    }
  };

  const migrateTestIds = async () => {
    try {
      const uid = user.uid;
      const batch = writeBatch(db);

      const mSnap = await getDocs(query(
        collection(db, 'matches'),
        where('isTest', '==', true),
        where('createdBy', '==', uid),
        limit(200)
      ));
      mSnap.forEach(d => {
        if (String(d.id).endsWith('-testMatch')) return;
        const newId = `${d.id}-testMatch`;
        batch.set(doc(db, 'matches', newId), d.data(), { merge: false });
        batch.delete(doc(db, 'matches', d.id));
      });

      const tSnap = await getDocs(query(
        collection(db, 'tournaments'),
        where('isTest', '==', true),
        where('createdBy', '==', uid),
        limit(200)
      ));
      tSnap.forEach(d => {
        if (String(d.id).endsWith('-testTourney')) return;
        const newId = `${d.id}-testTourney`;
        batch.set(doc(db, 'tournaments', newId), d.data(), { merge: false });
        batch.delete(doc(db, 'tournaments', d.id));
      });

      await batch.commit();
      Alert.alert('Migrated', 'Updated test doc IDs with suffixes.');
    } catch (e) {
      console.error(e);
      Alert.alert('Error', 'Failed to migrate test IDs.');
    }
  };

  // Printful webhook one-time setup. Calls Printful's API via our cloud
  // function with the live printfulWebhook v2 URL. Subscribes to
  // package_shipped + the fulfillment-failure events we handle. Safe to
  // run repeatedly — Printful overwrites the existing config.
  const configurePrintfulWebhook = async () => {
    try {
      const url = 'https://printfulwebhook-s3vhqbhvba-uc.a.run.app';
      const call = httpsCallable(functions, 'adminConfigurePrintfulWebhook');
      const result = await call({ url });
      const types = result?.data?.types?.join(', ') || '?';
      Alert.alert(
        'Webhook configured',
        `Printful will POST to:\n${url}\n\nEvent types: ${types}`
      );
    } catch (e) {
      console.error('configurePrintfulWebhook error:', e);
      Alert.alert('Webhook config failed', `${e?.code || 'error'} ${e?.message || ''}`);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView contentContainerStyle={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Admin Tools</Text>
          <Text style={styles.subtitle}>QA utilities and end-to-end testing</Text>
        </View>

        {/* Invite Code Management */}
        <TouchableOpacity
          style={styles.sectionHeader}
          onPress={() => setExpandedSections(prev => ({
            ...prev,
            inviteCodes: !prev.inviteCodes,
          }))}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Ionicons name="ticket-outline" size={20} color={COLORS.primary} />
            <Text style={styles.sectionTitle}>Invite Codes (Beta)</Text>
          </View>
          <Ionicons
            name={expandedSections.inviteCodes ? 'chevron-up' : 'chevron-down'}
            size={20}
            color={COLORS.textMuted}
          />
        </TouchableOpacity>
        {expandedSections.inviteCodes && <InviteCodeAdminPanel />}

        <TouchableOpacity
          style={{ backgroundColor: '#FCD000', padding: 12, borderRadius: 8, marginTop: 10 }}
          onPress={async () => {
            try {
              Alert.alert('Running', 'Auditing H2H match types...');
              const fn = httpsCallable(functions, 'auditH2HMatchTypes');
              const result = await fn();
              Alert.alert('Audit Complete', JSON.stringify(result.data, null, 2));
            } catch (e) {
              Alert.alert('Error', e.message);
            }
          }}
        >
          <Text style={{ color: '#000', fontWeight: 'bold', textAlign: 'center' }}>
            Audit H2H Match Types
          </Text>
        </TouchableOpacity>
        
        {/* Team Tournament E2E Tester */}
        <CollapsibleCard
          title="Team Tournament E2E Tester"
          icon="flask"
          iconColor={COLORS.primary}
          expanded={expandedSections.teamTournament}
          onToggle={() => toggleSection('teamTournament')}
          highlight
        >
          <TeamTournamentTester userId={user?.uid} />
        </CollapsibleCard>

        {/* Test Data */}
        <CollapsibleCard
          title="Legacy Test Data"
          icon="cube-outline"
          expanded={expandedSections.testData}
          onToggle={() => toggleSection('testData')}
        >
          <ToolBtn 
            label="Seed Test Matches & Tournaments" 
            onPress={seedTestData} 
            icon="add-circle-outline"
          />
          <ToolBtn 
            label="Clear Seeded Data" 
            onPress={clearSeedData} 
            danger 
            icon="trash-outline"
          />
          <ToolBtn 
            label="Migrate Test IDs to Suffixed" 
            onPress={migrateTestIds} 
            icon="swap-horizontal-outline"
          />
          <Text style={styles.help}>
            Seeds docs with IDs ending in <Text style={styles.highlight}>-testMatch</Text> / <Text style={styles.highlight}>-testTourney</Text> and sets <Text style={styles.highlight}>isTest: true</Text>.
          </Text>
        </CollapsibleCard>

        {/* Platform Account Disputes */}
        <CollapsibleCard
          title="Platform Account Disputes"
          icon="link-outline"
          expanded={expandedSections.platformDisputes}
          onToggle={() => {
            toggleSection('platformDisputes');
            if (!expandedSections.platformDisputes && platformDisputes.length === 0) {
              fetchPlatformDisputes();
            }
          }}
        >
          {disputesLoading ? (
            <ActivityIndicator size="small" color={COLORS.primary} style={{ padding: 20 }} />
          ) : platformDisputes.length === 0 ? (
            <View style={{ padding: 16 }}>
              <Text style={{ color: COLORS.textMuted, textAlign: 'center' }}>
                No pending disputes
              </Text>
              <TouchableOpacity
                onPress={fetchPlatformDisputes}
                style={{ marginTop: 12, alignSelf: 'center', padding: 8 }}
              >
                <Text style={{ color: COLORS.primary, fontWeight: '600' }}>Refresh</Text>
              </TouchableOpacity>
            </View>
          ) : (
            platformDisputes.map(dispute => (
              <View key={dispute.id} style={{
                backgroundColor: COLORS.cardAlt,
                borderRadius: 10,
                padding: 14,
                marginBottom: 10,
                borderWidth: 1,
                borderColor: COLORS.border,
              }}>
                <Text style={{ color: COLORS.text, fontWeight: '700', fontSize: 15, marginBottom: 4 }}>
                  {dispute.serviceId?.toUpperCase()}: {dispute.accountName}
                </Text>
                <Text style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 2 }}>
                  Filed by: {dispute.filedByDisplayName} ({dispute.reason})
                </Text>
                <Text style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 2 }}>
                  Current holder: {dispute.currentHolderDisplayName}
                </Text>
                {dispute.description ? (
                  <Text style={{ color: COLORS.textDim, fontSize: 12, marginBottom: 8, fontStyle: 'italic' }}>
                    "{dispute.description}"
                  </Text>
                ) : null}

                <View style={{ flexDirection: 'row', gap: 8, marginTop: 4 }}>
                  <TouchableOpacity
                    onPress={() => handleResolveDispute(dispute.id, 'transfer')}
                    disabled={resolvingDispute === dispute.id}
                    style={{
                      flex: 1, backgroundColor: COLORS.success, borderRadius: 8,
                      paddingVertical: 8, alignItems: 'center', opacity: resolvingDispute === dispute.id ? 0.5 : 1,
                    }}
                  >
                    <Text style={{ color: '#000', fontWeight: '700', fontSize: 12 }}>Transfer</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => handleResolveDispute(dispute.id, 'keep_current')}
                    disabled={resolvingDispute === dispute.id}
                    style={{
                      flex: 1, backgroundColor: COLORS.warning, borderRadius: 8,
                      paddingVertical: 8, alignItems: 'center', opacity: resolvingDispute === dispute.id ? 0.5 : 1,
                    }}
                  >
                    <Text style={{ color: '#000', fontWeight: '700', fontSize: 12 }}>Dismiss</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => handleResolveDispute(dispute.id, 'remove_both')}
                    disabled={resolvingDispute === dispute.id}
                    style={{
                      flex: 1, backgroundColor: COLORS.danger, borderRadius: 8,
                      paddingVertical: 8, alignItems: 'center', opacity: resolvingDispute === dispute.id ? 0.5 : 1,
                    }}
                  >
                    <Text style={{ color: '#fff', fontWeight: '700', fontSize: 12 }}>Remove Both</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))
          )}
        </CollapsibleCard>

        {/* Maintenance */}
        <CollapsibleCard
          title="Maintenance"
          icon="construct-outline"
          expanded={expandedSections.maintenance}
          onToggle={() => toggleSection('maintenance')}
        >
          <ToolBtn
            label="Configure Printful Webhook"
            onPress={configurePrintfulWebhook}
            icon="cloud-upload-outline"
          />
          <Text style={styles.help}>
            One-time setup. Points Printful at the live printfulWebhook
            function so package_shipped events fire tracking emails and
            capture COGS for merch orders. Safe to re-run; Printful
            overwrites the existing config.
          </Text>
        </CollapsibleCard>

        {/* Back Button */}
        <TouchableOpacity onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))} style={styles.backButton}>
          <Ionicons name="arrow-back" size={16} color={COLORS.textMuted} />
          <Text style={styles.back}>Back</Text>
        </TouchableOpacity>
      </ScrollView>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

/* ---- Collapsible Card ---- */
const CollapsibleCard = ({ title, icon, iconColor, expanded, onToggle, children, highlight }) => (
  <View style={[styles.card, highlight && styles.cardHighlight]}>
    <TouchableOpacity onPress={onToggle} style={styles.cardHeader} activeOpacity={0.7}>
      <View style={styles.cardHeaderLeft}>
        <Ionicons 
          name={icon} 
          size={18} 
          color={iconColor || (highlight ? COLORS.primary : COLORS.text)} 
        />
        <Text style={[styles.cardTitle, highlight && styles.cardTitleHighlight]}>
          {title}
        </Text>
      </View>
      <Ionicons 
        name={expanded ? 'chevron-up' : 'chevron-down'} 
        size={20} 
        color={COLORS.textMuted} 
      />
    </TouchableOpacity>
    {expanded && (
      <View style={styles.cardBody}>
        {children}
      </View>
    )}
  </View>
);

/* ---- Tool Button ---- */
const ToolBtn = ({ label, onPress, danger, icon }) => (
  <TouchableOpacity 
    onPress={onPress} 
    style={[styles.btn, danger ? styles.btnDanger : styles.btnNeutral]}
    activeOpacity={0.7}
  >
    {icon && (
      <Ionicons 
        name={icon} 
        size={16} 
        color={danger ? COLORS.danger : COLORS.text} 
        style={styles.btnIcon} 
      />
    )}
    <Text style={[styles.btnText, danger && styles.btnTextDanger]}>{label}</Text>
  </TouchableOpacity>
);

/* ---- styles ---- */
const styles = StyleSheet.create({
  safeArea: { 
    flex: 1, 
    backgroundColor: COLORS.bg,
  },
  container: { 
    padding: 16, 
    paddingBottom: 140,
  },
  center: { 
    flex: 1, 
    justifyContent: 'center', 
    alignItems: 'center',
    gap: 12,
  },
  dim: { 
    color: COLORS.textMuted,
  },
  warn: { 
    color: COLORS.danger,
    fontSize: 16,
    fontWeight: '600',
    marginTop: 8,
  },

  header: {
    marginBottom: 20,
  },
  title: { 
    color: COLORS.primary, 
    fontSize: 26, 
    fontWeight: '800',
  },
  subtitle: { 
    color: COLORS.textMuted, 
    fontSize: 14,
    marginTop: 4,
  },

  card: { 
    width: '100%', 
    maxWidth: 800, 
    backgroundColor: COLORS.card, 
    borderRadius: 12, 
    marginBottom: 12, 
    borderWidth: 1, 
    borderColor: COLORS.border,
    overflow: 'hidden',
  },
  cardHighlight: {
    borderColor: COLORS.primary + '50',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 14,
    paddingHorizontal: 16,
  },
  cardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  cardTitle: { 
    color: COLORS.text, 
    fontWeight: '700',
    fontSize: 14,
  },
  cardTitleHighlight: {
    color: COLORS.primary,
  },
  cardBody: {
    padding: 16,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
  },
  
  help: { 
    color: COLORS.textMuted, 
    marginTop: 12,
    fontSize: 12,
    lineHeight: 18,
  },
  highlight: {
    color: COLORS.primary,
  },

  btn: { 
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12, 
    paddingHorizontal: 14, 
    borderRadius: 8, 
    borderWidth: 1, 
    marginTop: 8,
  },
  btnNeutral: { 
    borderColor: COLORS.borderLight, 
    backgroundColor: COLORS.cardAlt,
  },
  btnDanger: { 
    borderColor: COLORS.danger + '40', 
    backgroundColor: COLORS.danger + '10',
  },
  btnIcon: {
    marginRight: 10,
  },
  btnText: { 
    color: COLORS.text, 
    fontWeight: '600',
    fontSize: 13,
  },
  btnTextDanger: {
    color: COLORS.danger,
  },

  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 24,
    paddingVertical: 8,
    alignSelf: 'flex-start',
  },
  back: { 
    color: COLORS.textMuted, 
    fontSize: 14,
  },
  backBtn: { 
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 16, 
    padding: 12,
    backgroundColor: COLORS.card,
    borderRadius: 8,
  },
  backText: { 
    color: COLORS.textMuted,
    fontWeight: '600',
  },
});